import Lottie from 'lottie-light-react';
import React, { useEffect, useState } from 'react';
import { useRewards } from '../Context/RewardProvider';
import { RewardType } from '../Types/RewardType';
import { LootBoxType, UserLootBox } from '../Types/UserLootBox';
import AttributeCardReward from './Rewards/AttributeCardReward';
import CoinsReward from './Rewards/CoinsReward';
import UniverseCreationReward from './Rewards/UniverseCreationReward';
import VideoGenerationReward from './Rewards/VideoGenerationReward';

interface LootBoxProps {
  lootBox: UserLootBox | null | undefined;
}

const LOOT_BOX_IMAGES = {
  [LootBoxType.Bronze]: (
    <Lottie
      animationData={require('../animations/chest-bronze.json')}
      loop={true}
      className="w-24 h-24"
    />
  ),
  [LootBoxType.Silver]: (
    <Lottie
      animationData={require('../animations/chest-silver.json')}
      loop={true}
      className="w-24 h-24"
    />
  ),
};

const LootBox: React.FC<LootBoxProps> = ({ lootBox }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { registerNewRewards } = useRewards();

  // Register rewards with the RewardProvider when the loot box is opened
  useEffect(() => {
    if (!isOpen) return;
    if (!lootBox) return;

    registerNewRewards(lootBox.rewards);
  }, [isOpen, lootBox]);

  if (lootBox === undefined) {
    return <LoadingLootBox />;
  }

  if (lootBox === null) {
    return <EmptyLootBox />;
  }

  const handleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4 pb-4">
      {!isOpen ? (
        <div onClick={handleClick} className="cursor-pointer">
          {LOOT_BOX_IMAGES[lootBox.type]}
          <p className="text-center text-sm text-gray-600 mt-2">
            Click to Open
          </p>
        </div>
      ) : (
        <div className="animate-fade-in flex flex-col items-center gap-2 w-full">
          <h2 className="text-l font-semibold text-center mb-2 text-gray-500">
            Here's what you earned:
          </h2>
          <div className="relative flex justify-center w-full px-4 py-4">
            {/* Card row with overlapping cards using flex and percentage-based margins */}
            <div className="flex justify-center items-center overflow-visible w-full">
              <div
                className="inline-flex items-center justify-center"
                style={{ width: `max(50%, ${lootBox.rewards.length * 70}px)` }}
              >
                {lootBox.rewards.map((reward, index) => {
                  const cardElement = (() => {
                    switch (reward.rewardType) {
                      case RewardType.CoinsStackL1:
                      case RewardType.CoinsStackL2:
                        return <CoinsReward key={reward.id} reward={reward} />;
                      case RewardType.UniverseCreation:
                        return (
                          <UniverseCreationReward
                            key={reward.id}
                            reward={reward}
                          />
                        );
                      case RewardType.Character:
                      case RewardType.Event:
                        return (
                          <AttributeCardReward
                            key={reward.id}
                            reward={reward}
                          />
                        );
                      case RewardType.VideoGeneration:
                        return (
                          <VideoGenerationReward
                            key={reward.id}
                            reward={reward}
                          />
                        );
                      default:
                        return null;
                    }
                  })();

                  return (
                    <div
                      key={reward.id}
                      className="card-wrapper transition-all duration-200 flex-none w-[140px] 
                        hover:-translate-y-2 hover:scale-110 hover:!z-[100]"
                      style={{
                        zIndex: 10 + index,
                        marginLeft: index === 0 ? 0 : '-3.5rem',
                      }}
                    >
                      {cardElement}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingLootBox: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-4 py-4">
    <div className="flex flex-col items-center gap-2">
      <div className="">
        <Lottie
          animationData={require('../animations/chest-deciding.json')}
          loop={true}
          className="w-64 my-[-6rem]"
        />
      </div>
      <div className="text-center text-sm text-gray-600">
        Determining rewards...
      </div>
    </div>
  </div>
);

const EmptyLootBox: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-4 py-4">
    <div className="flex flex-col items-center">
      <p className="text-center text-sm text-gray-500 max-w-[275px]">
        Get higher accuracy to earn rewards!
      </p>
    </div>
  </div>
);

export default LootBox;
